import { useMemo } from "react";
import { useT } from "../../i18n";
import { FehlerBand, Laedt, Marke } from "../../bausteine";
import { Zeilenblatt, Zweizeiler } from "../../bausteine/Handyformen";
import { SperrMarke } from "../../bausteine/Kundensperre";
import { Fehler } from "../../daten/klient";
import { useKunden } from "../../daten/haken";
import type { Kunde } from "../../daten/typen";

/* ══════════════════════════════════════════════════════════════════
   Kunden suchen — am Telefon.

   **Gesucht wird nach Firma und Ort** (16-interview.md, H4). Am Telefon
   sagt der eine „Hempen", der andere „die aus Garrel"; die Nummer liest
   ohnehin niemand aus dem Kopf vor. Die API sucht seit heute in allen
   dreien (`routen/kunden.ts`), gefiltert wird dort und nicht hier: Der
   Stamm kann größer sein, als in einen Abruf passt.

   **Die letzten Kunden zuerst.** Ohne Suchwort ist die alphabetische
   Liste die falsche: Wer im Auto sitzt, will den, bei dem er gestern
   war. Sortiert wird nach dem letzten Vorgang (`zuletztIso`) — am
   Schreibtisch bleibt die Liste, wie sie war.

   **Der Interessent in dreißig Sekunden** (Interview H9): Firma, Ort,
   Telefon. Mehr nicht — *„Wenn es heute ein Zettel ist, muss die
   Erfassung unter dreißig Sekunden dauern."* Alles Weitere steht im
   vollen Formular am Schreibtisch; wer es hier fragte, bekäme den
   Zettel zurück.
   ══════════════════════════════════════════════════════════════════ */

/** Die Reihenfolge fürs Auto: zuletzt etwas gehabt zuerst, dann alphabetisch. */
export function nachZuletzt(a: Kunde, b: Kunde): number {
  const x = a.zuletztIso ?? "";
  const y = b.zuletztIso ?? "";
  if (x !== y) return y.localeCompare(x);
  return a.name.localeCompare(b.name, "de");
}

/**
 * Suchfeld und Trefferliste.
 *
 * Das Feld steht offen und hat den Fokus, sobald das Blatt aufgeht
 * (`Blatt.tsx` setzt ihn auf das erste Eingabefeld) — die Tastatur ist damit
 * da, ohne dass jemand darauf tippt.
 */
export function Kundensuche({ wort, wortAendern, waehlen, gewaehlt, kopfzeile }: {
  wort: string;
  wortAendern: (neu: string) => void;
  waehlen: (k: Kunde) => void;
  /** Die Kundennummer, die schon gewählt ist — sie bekommt die Kante. */
  gewaehlt?: string | undefined;
  /** Was über der Liste steht, wenn nichts gesucht wird. */
  kopfzeile?: string | undefined;
}) {
  const t = useT();
  const suchwort = wort.trim();
  const { data, isPending, isError, error, refetch } = useKunden(suchwort || undefined);

  const treffer = useMemo(
    () => (data?.zeilen ?? []).slice().sort(nachZuletzt),
    [data],
  );

  return (
    <>
      <input
        className="hv-suche"
        type="search"
        inputMode="search"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="words"
        enterKeyHint="search"
        aria-label={t("handyKunden.sucheSchild")}
        placeholder={t("handyKunden.suchePlatz")}
        value={wort}
        onChange={(e) => wortAendern(e.target.value)}
      />

      {isError ? (
        <FehlerBand pfad={fehlerPfad(error)} fehler={error} nochmal={() => void refetch()} />
      ) : isPending ? (
        <Laedt zeilen={4} />
      ) : (
        <>
          {!suchwort && kopfzeile ? (
            <span className="hv-ueber">{kopfzeile}</span>
          ) : null}
          {/* **Dieselbe Zeile wie in jeder anderen Liste** (17.09.2026).
              Hier stand `hv-zeile`: Name links, Nummer rechts, 60 px hoch
              — eine siebte Form auf demselben Telefon. */}
          {/* **Die Zeile des Hauses, nicht die siebte Form** (18.09.2026).
              Hier stand `Handyzeile` mit rund 145 px — in einem Blatt von
              unten waren das eineinhalb Kunden. Luca hatte für die Kundenwahl
              ausdrücklich „nur wenige Kunden zum Scrollen" verlangt; mit dem
              `Zweizeiler` (56 px) sind es sechs. Oben der Name, darunter leise
              Nummer · Ort, und die Art als Marke rechts, weil sie die Zeile
              unterscheidet und nicht beschreibt. */}
          {treffer.length ? (
            <Zeilenblatt>
              {treffer.map((k) => (
                <Zweizeiler
                  key={k.nr}
                  punkt={gewaehlt === k.nr ? "gut" : "ruhig"}
                  beiKlick={() => waehlen(k)}
                  titel={k.name}
                  unter={[k.nr, k.ort || t("handyKunden.ohneOrt")]
                    .filter(Boolean)
                    .join(` ${t("allgemein.trennpunkt")} `)}
                  {...(k.gesperrt
                    // AG-04: gesperrt schlägt Interessent — es ist das, was man wissen muss.
                    ? { rechts: <SperrMarke kunde={k} /> }
                    : k.art === "interessent"
                      ? { rechts: <Marke>{t("handyKunden.interessent")}</Marke> }
                      : {})}
                />
              ))}
            </Zeilenblatt>
          ) : (
            <p className="handy-leer">
              {suchwort ? t("handyKunden.keinTreffer", { wort: suchwort }) : t("handyKunden.stammLeer")}
            </p>
          )}
        </>
      )}
    </>
  );
}

function fehlerPfad(e: unknown): string {
  return e instanceof Fehler ? e.textPfad : "fehler.unbekannt";
}

/* ══════════════ Was hier nicht mehr steht ══════════════

   **`InteressentSchnell` ist weg — 21.09.2026.** Firma, Ort, Telefon, und
   fertig; das war die Kundenanlage am Telefon, während am Schreibtisch
   `bausteine/KundeFormular.tsx` dieselbe Sache mit Anschrift,
   Ansprechpartner, E-Mail und den Pflichtfeldern des Betriebs (0098)
   erledigte.

   Luca, 21.09.2026: *„Die Interessentenanlage beim Aufmaß muss
   ausführlicher sein. Und Kundenanlage sowieso muss auch auf Mobile
   optimiert werden. Kannst dann das Formular auf Interessenten dann ja
   nehmen auch, das muss optimiert werden und shared sein. Jetzt hast du in
   Mobile ja quasi zwei Interessentenanlagen gebaut?"*

   **Es waren zwei, und die Folge stand auf dem Papier des Kunden:** Ein so
   angelegter Interessent hatte keine Straße und keine PLZ — und das Angebot
   an ihn trägt seine **Anschrift** und nicht einen Kontakt drüben
   (23-angebotsprozess.md, 16.09.2026). Es ging mit einer halben Adresse
   hinaus.

   Beide Telefonwege benutzen seitdem `KundeFormular` mit `form="blatt"`:
   `masken/vorgaenge/AngebotKurz.tsx` und `masken/handy/AufmassAnlegen.tsx`.
   Die 44-px-Ziele dafür stehen seit dem 17.09.2026 in `stile/kunden.css`
   und hängen am **Zeigegerät**, nicht an der Breite — das Formular war für
   das Telefon längst gerüstet, es wurde dort nur nicht benutzt. */
